/**
 * Crops an image to match the visible area inside a webcam preview.
 *
 * ### Why We Need This:
 * The `Webcam` component captures the **full camera input**, but the user only sees a portion
 * of it inside the preview (`350px x 180px`). If we don't crop it, the captured image will
 * include the **entire camera feed**, rather than just what was displayed inside the preview.
 *
 * ### How It Works:
 * 1. **Load the Full Image** - Since `getScreenshot()` captures the entire camera resolution,
 *    we first load the full-size image.
 * 2. **Compare Aspect Ratios** - The full image and preview box may have different dimensions.
 *    - If the full image is **wider** than the preview, we crop the sides.
 *    - If the full image is **taller**, we crop the top and bottom.
 * 3. **Calculate the Correct Crop Region** - Using dynamic scaling, we determine the exact
 *    `startX`, `startY`, `cropWidth`, and `cropHeight` values so that the cropped image
 *    matches what was visible inside the preview.
 * 4. **Return the Cropped Image** - We draw the cropped portion onto a canvas and return
 *    the new base64 image.
 *
 * @param {string} imageSrc - The base64-encoded image captured by the webcam.
 * @param {number} previewWidth - The width of the visible preview box (default: `350px`).
 * @param {number} previewHeight - The height of the visible preview box (default: `180px`).
 * @returns {Promise<string>} - A promise that resolves with the cropped base64 image.
 */
export const cropImage = (
	imageSrc: string,
	previewWidth: number = 500,
	previewHeight: number = 280,
): Promise<string> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.src = imageSrc;
		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			if (!ctx) {
				reject(new Error('Canvas context not available'));
				return;
			}

			// Full image dimensions (from the actual camera)
			const fullWidth = img.width;
			const fullHeight = img.height;

			// Webcam preview dimensions (fixed)
			const previewAspectRatio = previewWidth / previewHeight;
			const fullAspectRatio = fullWidth / fullHeight;

			let cropWidth, cropHeight, startX, startY;

			if (fullAspectRatio > previewAspectRatio) {
				// The full image is **wider** than the preview (landscape cameras)
				cropHeight = fullHeight; // Use the full height
				cropWidth = cropHeight * (previewWidth / previewHeight); // Match the aspect ratio

				startX = (fullWidth - cropWidth) / 2; // Center the crop horizontally
				startY = 0; // No need to adjust vertically
			} else {
				// The full image is **taller** than the preview (portrait cameras)
				cropWidth = fullWidth; // Use the full width
				cropHeight = cropWidth * (previewHeight / previewWidth); // Match the aspect ratio

				startX = 0; // No need to adjust horizontally
				startY = (fullHeight - cropHeight) / 2; // Center the crop vertically
			}

			// Set canvas size to cropped area
			canvas.width = cropWidth;
			canvas.height = cropHeight;

			// Draw the cropped section onto the canvas
			ctx.drawImage(
				img,
				startX,
				startY,
				cropWidth,
				cropHeight,
				0,
				0,
				cropWidth,
				cropHeight,
			);

			// Convert to base64
			resolve(canvas.toDataURL('image/jpeg'));
		};
		img.onerror = (error) => reject(error);
	});
};
